import { useRef, useState, type Dispatch, type SetStateAction } from "react";
import {
  deleteProject,
  generateProjectId,
  loadProject,
  saveProject,
  type StoredProject,
} from "../model/storage.ts";
import { dropMalformedLinkRows } from "../model/inp/parseInp.ts";
import type { ElementRef } from "../model/inp/properties.ts";

interface UseProjectLifecycleOptions {
  loaded: StoredProject | null;
  setLoaded: Dispatch<SetStateAction<StoredProject | null>>;
  refreshSavedProjects: () => void;
  reportStorageError: (err: unknown) => void;
  /** Called only when opening a fresh `.inp`/sample drops one or more malformed link rows (see `dropMalformedLinkRows`'s own header) — own `AlertDialog`, same pattern as `reportGeoreferenceWarnings`. */
  reportMalformedLinkWarnings: (warnings: string[]) => void;
  stopSimulation: () => void;
  setSelected: (ref: ElementRef | null) => void;
  setMultiSelected: (refs: ElementRef[]) => void;
  clearClipboard: () => void;
  setPlaceholderElements: Dispatch<SetStateAction<Set<string>>>;
  resetUndo: () => void;
  resetPendingCoalesce: () => void;
  resetLongitudinalProfile: () => void;
  resetResultsDashboard: () => void;
  resetNetworkTrace: () => void;
  resetMeasureTool: () => void;
  setMapMode: (mode: "plane" | "geo") => void;
  resetVisibleKinds: () => void;
}

/**
 * Opening a new/saved project, closing/renaming/duplicating/deleting the
 * current one, and the session-state reset every one of those (but NOT
 * renaming) shares — history/selection/clipboard from one project has no
 * meaning applied to another. `mapMode`/`visibleKinds` are deliberately
 * reset only from `openNewFile`/`openSavedProject` (they depend on the
 * INCOMING project's `crs`, or are a view preference worth preserving
 * across a duplicate), not from the shared reset helper below.
 */
export function useProjectLifecycle(opts: UseProjectLifecycleOptions) {
  // Set when a "/p/<id>" URL names a project no longer in IndexedDB.
  const [savedProjectNotFound, setSavedProjectNotFound] = useState(false);
  // Monotonic request id (same pattern as ProjectionPicker.tsx's own
  // debounced search, and the simulation worker's own identity guard) —
  // `openSavedProject` is triggered from Landing.tsx (no `disabled` gate)
  // and rapid `popstate` events (useProjectUrlSync.ts), and its own
  // `loadProject` is an async IndexedDB read with no ordering guarantee: an
  // earlier call's `loadProject` promise could resolve AFTER a later call's,
  // silently overwriting the just-requested project with a stale one (P1
  // deep-review).
  const openRequestIdRef = useRef(0);

  function resetProjectSessionState() {
    opts.stopSimulation();
    opts.setSelected(null);
    opts.setMultiSelected([]);
    opts.clearClipboard();
    opts.setPlaceholderElements(new Set());
    opts.resetUndo();
    opts.resetPendingCoalesce();
    opts.resetLongitudinalProfile();
    opts.resetResultsDashboard();
    opts.resetNetworkTrace();
    opts.resetMeasureTool();
  }

  async function openNewFile(
    rawSections: ReadonlyMap<string, string[][]>,
    comments: ReadonlyMap<string, string>,
    fileName: string,
    crs: string | null,
    name: string,
    ephemeral = false
  ) {
    const linkWarnings = new Set<string>();
    const cleanedRawSections = dropMalformedLinkRows(rawSections, linkWarnings);
    const stored: StoredProject = {
      id: generateProjectId(),
      name,
      fileName,
      lastModified: Date.now(),
      rawSections: cleanedRawSections,
      comments,
      crs,
      ephemeral,
    };
    try {
      await saveProject(stored);
    } catch (err) {
      opts.reportStorageError(err);
      return;
    }
    opts.refreshSavedProjects();
    resetProjectSessionState();
    opts.setLoaded(stored);
    opts.setMapMode(crs ? "geo" : "plane");
    opts.resetVisibleKinds();
    if (linkWarnings.size > 0) opts.reportMalformedLinkWarnings([...linkWarnings]);
  }

  async function openSavedProject(id: string) {
    const requestId = ++openRequestIdRef.current;
    let stored: StoredProject | undefined;
    try {
      stored = await loadProject(id);
    } catch (err) {
      if (requestId !== openRequestIdRef.current) return; // superseded by a newer open — this error is stale
      opts.reportStorageError(err);
      return;
    }
    if (requestId !== openRequestIdRef.current) return; // a newer openSavedProject call has since started
    if (!stored) {
      setSavedProjectNotFound(true);
      return;
    }
    setSavedProjectNotFound(false);
    resetProjectSessionState();
    opts.setLoaded(stored);
    opts.setMapMode(stored.crs ? "geo" : "plane");
    opts.resetVisibleKinds();
  }

  function closeProject() {
    // Flush immediately rather than waiting for the debounce: `setLoaded(null)`
    // below unmounts useAutosave.ts's effect (it's keyed on `loaded`), and
    // that effect's own cleanup clears its pending `setTimeout` — without an
    // explicit save first, up to AUTOSAVE_DEBOUNCE_MS of edits made right
    // before closing would be discarded, never actually written (same fix
    // EvalHid's own "Close project" button needed).
    if (opts.loaded) {
      void saveProject(opts.loaded).then(opts.refreshSavedProjects).catch(opts.reportStorageError);
    }
    resetProjectSessionState();
    opts.setLoaded(null);
  }

  function renameProject(name: string) {
    if (!opts.loaded) return;
    const updated = { ...opts.loaded, name, lastModified: Date.now() };
    opts.setLoaded(updated);
    void saveProject(updated).then(opts.refreshSavedProjects).catch(opts.reportStorageError);
  }

  /** "Save copy as" — a brand new saved project (its own id), same data, switches the open session to it. Always clears `ephemeral` even when duplicating a sample — this action's whole point is turning a throwaway session into a real, kept project. */
  function duplicateProject(name: string) {
    if (!opts.loaded) return;
    const copy: StoredProject = {
      ...opts.loaded,
      id: generateProjectId(),
      name,
      lastModified: Date.now(),
      ephemeral: false,
    };
    resetProjectSessionState();
    opts.setLoaded(copy);
    void saveProject(copy).then(opts.refreshSavedProjects).catch(opts.reportStorageError);
  }

  function deleteCurrentProject() {
    if (!opts.loaded) return;
    void deleteProject(opts.loaded.id)
      .then(opts.refreshSavedProjects)
      .catch(opts.reportStorageError);
    resetProjectSessionState();
    opts.setLoaded(null);
  }

  return {
    savedProjectNotFound,
    setSavedProjectNotFound,
    openNewFile,
    openSavedProject,
    closeProject,
    renameProject,
    duplicateProject,
    deleteCurrentProject,
  };
}
